import { Suspense } from "react";
import { Await } from "react-router";
import { ClientOnly } from "remix-utils/client-only";
import { Navigation, Pagination } from "swiper/modules";

import { useContentfulInspectorMode } from "@contentful/live-preview/react";

import { Carousel } from "~/components/ui/carousel";
import { Skeleton } from "~/components/ui/skeleton";
import { H2 } from "~/components/ui/text";
import type {
  ProductSearchHit,
  ProductSearchResponse,
  ProductSwatch,
} from "~/lib/app.types";
import type { SerializeFrom } from "~/lib/types";
import { usePick } from "~/lib/utils/screens";
import { useProductRecommendations } from "~/routes/resources/product-recommendations";

import { ProductTile } from "../../product-list/components/product-tile";

export const ProductRecommendations = (props: {
  recomendationData?: SerializeFrom<ProductSearchResponse>;
  sysId?: string;
  introspectId?: string;
  fields?:
    | {
        name: string;
        heading?: string;
        productIDs: string[];
        maxWidth?: 1280 | 1920 | 2560 | 768;
      }
    | {
        name: string;
        heading?: string;
        categoryId: string;
        maxWidth?: 1280 | 1920 | 2560 | 768;
      };
}) => {
  const inspectorProps = useContentfulInspectorMode({
    entryId: props.introspectId || props.sysId,
  });
  const { isLoading, data } = useProductRecommendations({
    sysId: props.sysId ?? "",
    categoryId:
      props.fields && "categoryId" in props.fields
        ? props.fields.categoryId
        : undefined,
    productIDs:
      props.fields && "productIDs" in props.fields
        ? props.fields.productIDs
        : [],
  });

  if (!props.recomendationData && isLoading) {
    return <Skeleton className="h-84 w-full md:h-[500px]" />;
  }

  if (!props.recomendationData && data) {
    return (
      <>
        <div className="" {...inspectorProps({ fieldId: "productIDs" })}>
          {props.fields?.heading && (
            <H2
              className="leading-lg text-brand-primary-black border-b-0 text-[26px] font-semibold lg:text-[32px]"
              {...inspectorProps({ fieldId: "heading" })}
            >
              {props.fields.heading}
            </H2>
          )}
          <RecommendationCarousel
            recomendationDataHits={data?.recommendations ?? []}
          />
        </div>
      </>
    );
  }

  return (
    <ClientOnly fallback={<Skeleton className="h-84 w-full md:h-[500px]" />}>
      {() => {
        return (
          <Suspense fallback={<div>Loading...</div>}>
            <Await resolve={props.recomendationData}>
              {recomendationData => (
                <RecommendationCarousel
                  recomendationDataHits={recomendationData?.hits}
                />
              )}
            </Await>
          </Suspense>
        );
      }}
    </ClientOnly>
  );
};

const RecommendationCarousel = (props: {
  recomendationDataHits?: (ProductSearchHit & {
    c_swatches?: ProductSwatch[];
  })[];
}) => {
  const pick = usePick();
  return (
    <Carousel
      className="mt-4"
      spaceBetween={20}
      slidesPerView={pick([1, 2, 2, 3, 4])}
      centeredSlides={false}
      pagination={{
        clickable: true,
      }}
      modules={[Pagination, Navigation]}
    >
      {props.recomendationDataHits?.map((hit, i) => (
        <ProductTile
          product={hit}
          key={`recommendation-${hit.productId}-${i}`}
          className="w-full md:w-full xl:w-full"
          itemScope
        />
      ))}
    </Carousel>
  );
};
